/**
 * Encryption Context
 *
 * Manages end-to-end encryption state throughout the application.
 * Handles passphrase setup, session unlocking, and encryption operations.
 * Public/marketing routes skip the encryption-status blocker per 2026-08-17.
 *
 * Features:
 * - Auto-detects encryption status on mount
 * - Shows setup modal for new users
 * - Shows unlock modal for returning users
 * - Manages encryption key in memory
 * - Provides encrypt/decrypt helpers to components
 * - Graceful degradation for encryption service failures (502 errors)
 * - Staging gate integration (skips modals when staging access required)
 *
 * @fileoverview Encryption state management context
 */

import { useAuth, useClerk } from '@clerk/clerk-react';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { apiClient, type EncryptionStatus } from '../api';
import { PassphraseEntry } from '../components/passphrase-entry';
import { PassphraseSetup } from '../components/passphrase-setup';
import * as caseKeyService from '../services/caseKeyService';
import {
  type ReEncryptionResult,
  reEncryptCaseToEnvelope,
  reEncryptIntakeRequestToEnvelope,
} from '../services/caseReEncryptionService';
import { encryptionService } from '../services/encryptionService';
import { useAuth as useAppAuth } from './auth-context';

// Sentinels returned from decryptField when the value cannot be revealed.
// Exported so consumers can detect them and substitute a graceful fallback
// (e.g., show a record id instead of the raw error string in UI lists).
//
// IMPORTANT: changing these values is a breaking change for any code that
// detects them via string match. Update consumers in lockstep.
export const DECRYPTION_SENTINELS = {
  // Encryption is set up but the user hasn't unlocked it yet, or there's no
  // case key available for a CASEKEY: field.
  ENCRYPTED_LOCKED: '[Encrypted]',
  // CASEKEY field is present but the case key isn't accessible (e.g., access
  // was not granted to this user).
  NO_ACCESS: '[No access]',
  // RSA decryption of an ENC:/HENC: field threw — typically because the
  // doctor's key has rotated, the record predates current encryption setup,
  // or the field is in a legacy format and needs re-encryption.
  DECRYPTION_FAILED: '[Unable to decrypt — record may need re-encryption]',
} as const;

export function isDecryptionSentinel(value: string | null | undefined): boolean {
  if (!value) return false;
  return (
    value === DECRYPTION_SENTINELS.ENCRYPTED_LOCKED ||
    value === DECRYPTION_SENTINELS.NO_ACCESS ||
    value === DECRYPTION_SENTINELS.DECRYPTION_FAILED
  );
}

interface EncryptionContextValue {
  // Status
  status: EncryptionStatus | null;
  isUnlocked: boolean;
  isLoading: boolean;
  hasServiceError: boolean; // New: tracks if encryption service is unavailable
  isShowingModal: boolean; // True when PassphraseSetup or PassphraseEntry modal is visible

  // Actions
  setupEncryption: (passphrase: string, options?: { silent?: boolean }) => Promise<void>;
  unlockEncryption: (passphrase: string) => Promise<void>;
  lockEncryption: () => void;
  clearKeyMaterial: () => void;
  refreshStatus: () => Promise<void>;
  retryStatusLoad: () => Promise<void>; // New: manual retry for users
  suppressSetupModal: () => void; // Prevent auto-show of PassphraseSetup modal (used by signup forms)

  // Encryption helpers
  encryptPayload: (data: object) => Promise<string>;
  decryptPayload: (ciphertext: string) => Promise<object>;
  decryptWithPrivateKey: (ciphertext: string) => Promise<object>;

  // Field-level encryption (caseId enables CASEKEY: decryption for envelope-encrypted fields)
  decryptField: (encryptedValue: string | null | undefined, caseId?: string) => Promise<string>;

  // Envelope encryption (per-case keys)
  getCaseKey: (caseId: string) => Promise<CryptoKey | null>;
  createCaseEnvelope: (caseId: string) => Promise<CryptoKey>;
  grantCaseAccess: (
    caseId: string,
    targetUserId: string,
    targetPublicKeyPem?: string,
  ) => Promise<void>;
  encryptForCase: (caseId: string, data: object) => Promise<string>;
  decryptForCase: <T extends object = object>(caseId: string, ciphertext: string) => Promise<T>;
  clearCaseKeyCache: () => void;

  // Re-encryption (RSA → envelope). Returns BOTH the successfully
  // re-encrypted fields AND the list of fields that couldn't be decrypted
  // with the current RSA key — callers must surface failedFields so the
  // user has a recovery path (see 2026-05-14 incident).
  reEncryptCaseFields: (
    caseId: string,
    rawCaseData: Record<string, any>,
  ) => Promise<ReEncryptionResult>;
  reEncryptIntakeRequestFields: (
    caseId: string,
    rawRequestData: Record<string, any>,
  ) => Promise<ReEncryptionResult>;
}

const EncryptionContext = createContext<EncryptionContextValue | null>(null);

interface EncryptionProviderProps {
  children: ReactNode;
  /**
   * Whether to require encryption setup/unlock before showing app.
   * Set to false to allow users to skip encryption (for optional E2EE).
   */
  requireEncryption?: boolean;
}

export function EncryptionProvider({
  children,
  requireEncryption = true, // Show unlock modal on every session
}: EncryptionProviderProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const { signOut } = useClerk();
  const { user: appUser } = useAppAuth();
  const [status, setStatus] = useState<EncryptionStatus | null>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasServiceError, setHasServiceError] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  // SECURITY NOTE: The private key PEM is held in React state (JS heap) for the session duration.
  // Ideally this would use a non-extractable CryptoKey via SubtleCrypto, but that requires
  // a larger refactor of the RSA wrapping/unwrapping flow. For now, the key is cleared on
  // lock/signout/session-end. Do NOT log or expose this value.
  const [cachedPrivateKeyPem, setCachedPrivateKeyPem] = useState<string | null>(null);
  const caseKeyCache = useRef<Map<string, CryptoKey>>(new Map());
  // Promise-based cache to deduplicate concurrent getCaseKey calls (thundering herd prevention)
  const caseKeyPendingRequests = useRef<Map<string, Promise<CryptoKey | null>>>(new Map());
  // Cache epoch: incremented on clear to prevent stale in-flight promises from repopulating
  const caseKeyEpoch = useRef(0);
  // Suppresses the auto-show of PassphraseSetup modal (set by signup forms doing their own setup)
  const setupModalSuppressed = useRef(false);
  // Reentrancy guard to prevent concurrent setupEncryption calls
  const setupInProgress = useRef(false);
  // Reentrancy guard to prevent concurrent loadEncryptionStatus calls
  const loadStatusInProgress = useRef(false);

  // Clear encryption keys on new browser session (using sessionStorage)
  useEffect(() => {
    const SESSION_KEY = 'expertmd_encryption_session';
    const hasExistingSession = sessionStorage.getItem(SESSION_KEY);

    if (!hasExistingSession) {
      // New browser session - clear encryption keys
      encryptionService.clearKey();
      setCachedPrivateKeyPem(null);
      caseKeyCache.current.clear();
      sessionStorage.setItem(SESSION_KEY, 'true');
      console.log('[EncryptionContext] New session detected - cleared encryption keys');
    }
  }, []);

  // Check if current route is a public page that should skip encryption prompts
  const isPublicIntakePage = () => {
    // Safety check for window object (in case this runs in a non-browser context)
    if (typeof window === 'undefined') {
      return false;
    }

    const pathname = window.location.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);

    if (pathSegments.length === 0) return false;

    // Public routes that must never show encryption modals:
    // /intake/:vanityUrl, /client-intake/:vanityUrl, /attorney/*, /team/signup
    const publicPrefixes = ['intake', 'client-intake', 'attorney', 'team'];
    if (publicPrefixes.includes(pathSegments[0])) {
      return true;
    }

    // Single segment vanity URL (e.g., /dr-smith) — exclude known app routes
    const RESERVED_PATHS = new Set([
      'auth',
      'dashboard',
      'requests',
      'cases',
      'invoices',
      'billing',
      'settings',
      'admin',
      'system-status',
      'terms',
      'privacy',
      'physicians',
      'compare',
      'side-income',
      'blog',
      'how-it-works',
      'pricing',
      'security',
      'attorneys',
      'appointments',
      'intake-requests',
      'sign-up',
    ]);
    if (
      pathSegments.length === 1 &&
      /^[a-zA-Z0-9-]+$/i.test(pathSegments[0]) &&
      !RESERVED_PATHS.has(pathSegments[0].toLowerCase())
    ) {
      return true;
    }

    return false;
  };

  // Check if staging gate is required (staging protection enabled but access not granted)
  const checkStagingAccess = async () => {
    try {
      const stagingAccessGranted = sessionStorage.getItem('staging_access_granted') === 'true';
      if (stagingAccessGranted) {
        return { needsStagingAccess: false };
      }

      const { enabled } = await apiClient.checkStagingAccess();
      return { needsStagingAccess: enabled };
    } catch (error) {
      console.warn('[EncryptionContext] Failed to check staging access:', error);
      // Assume no staging protection on error (fail open)
      return { needsStagingAccess: false };
    }
  };

  // Load encryption status on mount (only if signed in)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dep list; loader fn identity changes every render (pre-existing pattern)
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      loadEncryptionStatus();
    } else if (isLoaded && !isSignedIn) {
      // User not signed in, clear encryption state and keys from memory
      encryptionService.clearKey();
      setCachedPrivateKeyPem(null);
      caseKeyCache.current.clear();
      setStatus(null);
      setIsUnlocked(false);
      setIsLoading(false);
      setHasServiceError(false);
      console.log('[EncryptionContext] User signed out - cleared encryption keys');
    }
  }, [isLoaded, isSignedIn]);

  /**
   * Load Encryption Status with Retry Logic and Graceful Degradation
   *
   * Handles HTTP 502 errors gracefully by:
   * 1. Retrying with exponential backoff (3 attempts)
   * 2. Providing fallback state when service is unavailable
   * 3. Allowing app usage with encryption disabled
   * 4. Showing user-friendly error messages
   *
   * Also handles staging gate integration by skipping encryption modals
   * when staging access is required but not yet granted.
   */
  const loadEncryptionStatus = async () => {
    if (loadStatusInProgress.current) {
      return;
    }
    loadStatusInProgress.current = true;

    try {
      setIsLoading(true);
      setHasServiceError(false);

      const MAX_RETRIES = 3;
      const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          const encryptionStatus = await apiClient.getEncryptionStatus();
          setStatus(encryptionStatus);
          setHasServiceError(false); // Clear any previous error state

          // Skip encryption modals on public intake pages
          const isPublicPage = isPublicIntakePage();
          if (isPublicPage) {
            console.log(
              '[EncryptionContext] Public intake page detected - skipping encryption prompts',
            );
            setIsLoading(false);
            return;
          }

          // Check if staging gate is required and skip encryption modals
          const { needsStagingAccess } = await checkStagingAccess();
          if (needsStagingAccess) {
            console.log(
              '[EncryptionContext] Staging access required - skipping encryption prompts until staging gate resolved',
            );
            setIsLoading(false);
            return;
          }

          // Determine what modal to show (if any) - only for signed in users
          // Always clear both before setting one to ensure mutual exclusivity
          if (isSignedIn) {
            if (!encryptionStatus.setup) {
              // Not initialized - show setup modal if required (unless suppressed by signup form)
              if (requireEncryption && !setupModalSuppressed.current && !setupInProgress.current) {
                setShowUnlock(false);
                setShowSetup(true);
              }
            } else if (encryptionStatus.setup && !encryptionService.hasKey()) {
              // Initialized but not unlocked - show unlock modal if required
              if (requireEncryption) {
                setShowSetup(false);
                setShowUnlock(true);
              }
            } else if (encryptionService.hasKey()) {
              // Already unlocked
              setShowSetup(false);
              setShowUnlock(false);
              setIsUnlocked(true);
            }
          }

          setIsLoading(false);
          return; // Success - exit retry loop
        } catch (error: any) {
          const isLastAttempt = attempt === MAX_RETRIES - 1;
          const isRetryableError =
            error?.status === 500 ||
            error?.status === 502 ||
            error?.status === 503 ||
            error?.status === 504;

          console.error(
            `[EncryptionContext] Failed to load encryption status (attempt ${attempt + 1}/${MAX_RETRIES}):`,
            error,
          );

          // If this is a retryable server error and not the last attempt, wait and retry
          if (isRetryableError && !isLastAttempt) {
            const delay = RETRY_DELAYS[attempt];
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue; // Continue to next retry attempt
          }

          // Final attempt failed or non-retryable error - enter graceful degradation mode
          console.error(
            '[EncryptionContext] All retry attempts failed or non-retryable error, entering graceful degradation mode',
          );

          setHasServiceError(true);
          setStatus(null); // Clear status to indicate service unavailable
          setIsLoading(false);

          // SECURITY NOTE: Graceful degradation intentionally allows unencrypted operation
          // when the encryption service is unavailable (fail-open). This is a deliberate
          // availability-over-confidentiality trade-off. If strict encryption is required,
          // change this to block app usage when encryption service is unreachable (fail-closed).
          // Provide fallback state - allow app usage without encryption
          if (isRetryableError) {
            toast.error('Encryption service temporarily unavailable', {
              description:
                "The app will work normally, but encryption features are disabled. We'll retry automatically.",
              duration: 8000,
              action: {
                label: 'Retry Now',
                onClick: () => retryStatusLoad(),
              },
            });
          } else {
            toast.error('Failed to load encryption status', {
              description:
                'There was an issue checking encryption settings. Some features may be limited.',
              duration: 6000,
            });
          }

          return; // Exit retry loop
        }
      }
    } finally {
      loadStatusInProgress.current = false;
    }
  };

  /**
   * Manual Retry for Encryption Status Loading
   *
   * Allows users to manually retry loading encryption status
   * after a service failure.
   */
  const retryStatusLoad = async () => {
    toast.info('Retrying encryption service connection...');
    const hadError = hasServiceError;
    await loadEncryptionStatus();
    // Check the ref-like latest value via a setState callback to avoid stale closure
    setHasServiceError((current) => {
      if (hadError && !current) {
        toast.success('Encryption service reconnected successfully!');
      }
      return current;
    });
  };

  /**
   * Setup Encryption (First Time)
   *
   * 1. Generate RSA key pair for public key encryption
   * 2. Initialize encryption on backend (get salt, store public key)
   * 3. Encrypt private key with passphrase
   * 4. Send encrypted private key to backend
   * 5. Derive symmetric key from passphrase + salt
   * 6. Store keys in memory
   */
  const setupEncryption = async (passphrase: string, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;

    // Reentrancy guard — prevent concurrent calls from corrupting keys
    if (setupInProgress.current) {
      console.warn(
        '[EncryptionContext] setupEncryption already in progress, skipping duplicate call',
      );
      return;
    }
    setupInProgress.current = true;

    try {
      // Step 1: Generate RSA key pair
      if (!silent) {
        toast.info('Generating encryption keys...', {
          description: 'This may take a few seconds.',
          duration: 10000,
        });
      }

      const { publicKey, privateKey } = await encryptionService.generateKeyPair();

      // Step 2: Export keys to PEM format
      const publicKeyPem = await encryptionService.exportPublicKey(publicKey);
      const privateKeyPem = await encryptionService.exportPrivateKey(privateKey);

      // Step 3: Initialize encryption on backend (get salt, store public key)
      const { salt } = await apiClient.initializeEncryption(publicKeyPem, null);

      // Convert base64 salt to Uint8Array
      const saltBytes = Uint8Array.from(atob(salt), (c) => c.charCodeAt(0));

      // Step 4: Encrypt private key with passphrase
      const encryptedPrivateKey = await encryptionService.encryptPrivateKeyWithPassphrase(
        privateKeyPem,
        passphrase,
        saltBytes,
      );

      // Step 5: Update backend with encrypted private key
      await apiClient.updateEncryptedPrivateKey(encryptedPrivateKey);

      // Step 6: Derive symmetric encryption key from passphrase + salt
      const key = await encryptionService.deriveKey(passphrase, saltBytes);

      // Store key in memory
      encryptionService.setKey(key);

      // Cache the private key PEM for later use
      setCachedPrivateKeyPem(privateKeyPem);

      setIsUnlocked(true);
      setShowSetup(false);

      // Refresh status WITHOUT triggering the loading spinner (background fetch)
      try {
        const refreshedStatus = await apiClient.getEncryptionStatus();
        setStatus(refreshedStatus);
      } catch {
        // Non-critical: status will refresh on next page load
      }

      if (!silent) {
        toast.success('Encryption enabled successfully!', {
          description: 'Your case data will now be encrypted end-to-end.',
        });
      }
    } catch (error) {
      console.error('[EncryptionContext] Setup failed:', error);
      // Reset suppression so the modal can show again on retry/next login
      setupModalSuppressed.current = false;
      throw error; // Let caller handle error display
    } finally {
      setupInProgress.current = false;
    }
  };

  /**
   * Unlock Encryption (Returning User)
   *
   * 1. Get salt from backend
   * 2. Derive key from passphrase + salt
   * 3. Decrypt private key and cache it
   * 4. Store key in memory
   */
  const unlockEncryption = async (passphrase: string) => {
    try {
      // Get salt and encryption status from backend
      const [{ salt }, encryptionStatus] = await Promise.all([
        apiClient.getEncryptionSalt(),
        apiClient.getEncryptionStatus(),
      ]);

      // Convert base64 salt to Uint8Array
      const saltBytes = Uint8Array.from(atob(salt), (c) => c.charCodeAt(0));

      // Derive encryption key from passphrase + salt
      const key = await encryptionService.deriveKey(passphrase, saltBytes);

      // Store key in memory
      encryptionService.setKey(key);

      // If user has encrypted private key, decrypt and cache it
      if (encryptionStatus.encryptedPrivateKey) {
        const privateKeyPem = await encryptionService.decryptPrivateKeyWithPassphrase(
          encryptionStatus.encryptedPrivateKey,
          passphrase,
          saltBytes,
        );
        setCachedPrivateKeyPem(privateKeyPem);
      }

      setIsUnlocked(true);
      setShowUnlock(false);

      toast.success('Encryption unlocked', {
        description: 'You can now access your encrypted case data.',
      });
    } catch (error) {
      console.error('[EncryptionContext] Unlock failed:', error);
      // Clear the wrong key that was set before the decryption attempt failed
      encryptionService.clearKey();
      throw error; // Let modal handle error display
    }
  };

  /**
   * Lock Encryption
   *
   * Clears the encryption key and private key from memory.
   * User will need to enter passphrase again to access encrypted data.
   */
  const lockEncryption = () => {
    clearKeyMaterial();

    // Only show unlock modal for authenticated users (skip for public intake pages)
    // Also skip if encryption service has errors
    const isPublicPage = isPublicIntakePage();
    if (requireEncryption && status?.setup && isSignedIn && !isPublicPage && !hasServiceError) {
      setShowUnlock(true);
    }

    toast.info('Encryption locked', {
      description: 'Your encryption key has been cleared from memory.',
    });
  };

  /**
   * Clear Key Material (silent teardown)
   *
   * Drops every piece of decrypted key material held in browser memory —
   * the derived symmetric key, the decrypted private key PEM, and all
   * unwrapped per-case CryptoKeys — without showing any unlock UI or toast.
   * Bumping the epoch prevents in-flight case-key requests from
   * repopulating the cache after teardown. Used for automatic logoff
   * (HIPAA §164.312(a)(2)(iii)) before signing the user out.
   */
  const clearKeyMaterial = () => {
    encryptionService.clearKey();
    setCachedPrivateKeyPem(null);
    caseKeyEpoch.current++;
    caseKeyCache.current.clear();
    caseKeyPendingRequests.current.clear();
    setIsUnlocked(false);
  };

  /**
   * Refresh Encryption Status
   */
  const refreshStatus = async () => {
    await loadEncryptionStatus();
  };

  /**
   * Encrypt Payload Helper
   *
   * Convenience wrapper around encryptionService.encryptPayload
   * Throws error if not unlocked.
   */
  const encryptPayload = async (data: object): Promise<string> => {
    if (!isUnlocked) {
      throw new Error('Encryption not unlocked. Please enter your passphrase.');
    }

    return await encryptionService.encryptPayload(data);
  };

  /**
   * Decrypt Payload Helper
   *
   * Convenience wrapper around encryptionService.decryptPayload
   * Throws error if not unlocked.
   */
  const decryptPayload = async (ciphertext: string): Promise<object> => {
    if (!isUnlocked) {
      throw new Error('Encryption not unlocked. Please enter your passphrase.');
    }

    return await encryptionService.decryptPayload(ciphertext);
  };

  /**
   * Decrypt With Private Key Helper
   *
   * Decrypts RSA-encrypted data using the user's private key.
   * The private key must be cached in memory (happens during setup/unlock).
   * Throws error if not unlocked or private key not available.
   */
  const decryptWithPrivateKey = async (ciphertext: string): Promise<object> => {
    if (!isUnlocked) {
      throw new Error('Encryption not unlocked. Please enter your passphrase.');
    }

    if (!cachedPrivateKeyPem) {
      throw new Error('Private key not available. Please unlock encryption again.');
    }

    try {
      // Use cached private key to decrypt RSA-encrypted data
      return await encryptionService.decryptWithPrivateKey(ciphertext, cachedPrivateKeyPem);
    } catch (error) {
      console.error('[EncryptionContext] Decrypt with private key failed:', error);
      throw error;
    }
  };

  /**
   * Decrypt a single field that was encrypted with field-level encryption.
   * Supports three formats:
   *   - CASEKEY: prefix → decrypt with per-case AES key (requires caseId)
   *   - ENC:/HENC: prefix → decrypt with RSA private key
   *   - No prefix → return as-is (plaintext)
   *
   * @param encryptedValue  The field value (may be plaintext, ENC:, HENC:, or CASEKEY:)
   * @param caseId          Optional case ID — required for CASEKEY: fields
   */
  const decryptField = async (
    encryptedValue: string | null | undefined,
    caseId?: string,
  ): Promise<string> => {
    // Return as-is if null/undefined
    if (!encryptedValue) {
      return encryptedValue || '';
    }

    // Route: CASEKEY: prefix → envelope decryption via case key
    if (encryptedValue.startsWith('CASEKEY:')) {
      if (!isUnlocked || !cachedPrivateKeyPem) {
        return DECRYPTION_SENTINELS.ENCRYPTED_LOCKED;
      }
      if (!caseId) {
        console.warn('[EncryptionContext] CASEKEY: field requires caseId for decryption');
        return DECRYPTION_SENTINELS.ENCRYPTED_LOCKED;
      }
      try {
        const caseKey = await getCaseKey(caseId);
        if (!caseKey) {
          console.warn('[EncryptionContext] No case key available for caseId:', caseId);
          return DECRYPTION_SENTINELS.NO_ACCESS;
        }
        const result = await caseKeyService.decryptFieldWithCaseKey(encryptedValue, caseKey);
        return result;
      } catch (error) {
        console.error('[EncryptionContext] CASEKEY field decryption failed:', error);
        return DECRYPTION_SENTINELS.ENCRYPTED_LOCKED;
      }
    }

    // Route: ENC:/HENC: prefix → RSA field-level decryption
    if (encryptedValue.startsWith('ENC:') || encryptedValue.startsWith('HENC:')) {
      if (!isUnlocked || !cachedPrivateKeyPem) {
        return DECRYPTION_SENTINELS.ENCRYPTED_LOCKED;
      }
      try {
        const decrypted = await encryptionService.decryptField(encryptedValue, cachedPrivateKeyPem);
        return decrypted;
      } catch (error) {
        console.error('[EncryptionContext] Field decryption failed:', error);
        // Symptom from prod 2026-05-14: legacy ENC: plaintiff fields surface
        // here when the record predates the CASEKEY: migration but other
        // fields on the same record were migrated. See
        // docs/incidents/2026-05-14-pre-screening-decryption-failed.md.
        return DECRYPTION_SENTINELS.DECRYPTION_FAILED;
      }
    }

    // No recognized prefix → plaintext, return as-is
    return encryptedValue;
  };

  // ===========================================================================
  // Envelope Encryption — Per-Case Key Operations
  // ===========================================================================

  /**
   * Get (or fetch + unwrap + cache) the case key for a given case.
   * Returns null if no envelope exists for the current user on this case.
   */
  const getCaseKey = async (caseId: string): Promise<CryptoKey | null> => {
    // Check resolved cache first
    const cached = caseKeyCache.current.get(caseId);
    if (cached) return cached;

    // Check if there's already a pending request for this caseId (thundering herd prevention)
    const pending = caseKeyPendingRequests.current.get(caseId);
    if (pending) return pending;

    if (!cachedPrivateKeyPem) {
      console.warn('[EncryptionContext] Cannot get case key — private key not available');
      return null;
    }

    // Capture epoch at fetch start — if cache is cleared while in-flight, discard result
    const epochAtStart = caseKeyEpoch.current;

    // Create a single promise that all concurrent callers will share
    const fetchPromise = (async (): Promise<CryptoKey | null> => {
      try {
        const envelope = await apiClient.getMyCaseEnvelope(caseId);
        if (!envelope) return null;

        const caseKey = await caseKeyService.unwrapCaseKey(
          envelope.encryptedCaseKey,
          cachedPrivateKeyPem,
        );

        // Only populate cache if epoch hasn't changed (no clear/lock happened in between)
        if (caseKeyEpoch.current === epochAtStart) {
          caseKeyCache.current.set(caseId, caseKey);
        }
        return caseKey;
      } catch (error) {
        console.error('[EncryptionContext] Failed to get case key:', error);
        return null;
      } finally {
        // Remove from pending regardless of success/failure
        caseKeyPendingRequests.current.delete(caseId);
      }
    })();

    caseKeyPendingRequests.current.set(caseId, fetchPromise);
    return fetchPromise;
  };

  /**
   * Create a new case envelope: generate case key, wrap with own public key, store.
   * Returns the CryptoKey for immediate use (also cached).
   * Call this after case creation to set up the doctor's envelope.
   */
  const createCaseEnvelope = async (caseId: string): Promise<CryptoKey> => {
    if (!isUnlocked || !cachedPrivateKeyPem) {
      throw new Error('Encryption not unlocked. Please enter your passphrase.');
    }

    if (!status?.setup) {
      throw new Error('Encryption not set up. Please complete encryption setup first.');
    }

    const currentUserId = appUser?.id;
    if (!currentUserId) {
      throw new Error('User profile not loaded. Please try again.');
    }

    // Idempotency: check if an envelope already exists on the server before generating a new key.
    // This prevents the dual-tab race where two tabs generate different keys and the second
    // upsert overwrites the first, causing data encrypted with the first key to become unrecoverable.
    try {
      const existingEnvelope = await apiClient.getMyCaseEnvelope(caseId);
      if (existingEnvelope) {
        const existingKey = await caseKeyService.unwrapCaseKey(
          existingEnvelope.encryptedCaseKey,
          cachedPrivateKeyPem,
        );
        caseKeyCache.current.set(caseId, existingKey);
        return existingKey;
      }
    } catch {
      // 404 or error means no existing envelope — proceed to create one
    }

    // Generate fresh AES-256-GCM case key
    const caseKey = await caseKeyService.generateCaseKey();

    // Fetch own public key for wrapping
    const { publicKey: ownPublicKeyPem } = await apiClient.getUserPublicKey(currentUserId);
    if (!ownPublicKeyPem) {
      throw new Error('Own public key not available');
    }

    // Wrap case key with own public key
    const wrappedCaseKey = await caseKeyService.wrapCaseKey(caseKey, ownPublicKeyPem);

    // Store envelope on server
    await apiClient.createCaseEnvelope(caseId, currentUserId, wrappedCaseKey);

    // Cache the case key for immediate use
    caseKeyCache.current.set(caseId, caseKey);

    // Auto-grant envelope access to accepted team members with encryption enabled
    try {
      const teamData = await apiClient.getTeamMembers();
      const encryptedMembers = teamData.members.filter(
        (m) => m.status === 'ACCEPTED' && m.userId && m.encryptionEnabled,
      );

      for (const member of encryptedMembers) {
        try {
          const { publicKey: memberPublicKeyPem } = await apiClient.getUserPublicKey(
            member.userId!,
          );
          if (memberPublicKeyPem) {
            const wrappedForMember = await caseKeyService.wrapCaseKey(caseKey, memberPublicKeyPem);
            await apiClient.createCaseEnvelope(caseId, member.userId!, wrappedForMember);
          }
        } catch (memberError) {
          // Non-fatal: team member envelope grant failed, they can request access later
          console.warn(
            `Failed to auto-grant envelope to team member ${member.userId}:`,
            memberError,
          );
        }
      }
    } catch (teamError) {
      // Non-fatal: team member lookup failed
      console.warn('Failed to auto-grant envelopes to team members:', teamError);
    }

    return caseKey;
  };

  /**
   * Grant another user access to a case by wrapping the case key with their public key.
   * The current user must already have access (be the case owner).
   */
  const grantCaseAccess = async (
    caseId: string,
    targetUserId: string,
    preFetchedPublicKeyPem?: string,
  ): Promise<void> => {
    if (!isUnlocked || !cachedPrivateKeyPem) {
      throw new Error('Encryption not unlocked. Please enter your passphrase.');
    }

    // Get the case key (unwrap our own envelope)
    const caseKey = await getCaseKey(caseId);
    if (!caseKey) {
      throw new Error('No case key available — you may not have access to this case.');
    }

    // Use pre-fetched public key if provided, otherwise fetch from API
    let targetPublicKeyPem = preFetchedPublicKeyPem;
    if (!targetPublicKeyPem) {
      const { publicKey } = await apiClient.getUserPublicKey(targetUserId);
      targetPublicKeyPem = publicKey ?? undefined;
    }
    if (!targetPublicKeyPem) {
      throw new Error('Target user has not set up encryption.');
    }

    // We need to re-wrap the case key with the target's public key.
    // Since the case key is non-extractable (from unwrapCaseKey), we can't use wrapKey directly.
    // Solution: re-generate a temporary extractable copy by wrapping/unwrapping through our own key.
    // Actually — the generateCaseKey returns extractable:true, but unwrapCaseKey returns extractable:false.
    // For granting access, we need to wrap with a new public key. Since our unwrapped key is non-extractable,
    // we need to re-wrap it. But wrapKey requires extractable:true on the key being wrapped.
    //
    // Workaround: Store the extractable case key during createCaseEnvelope (only the creator has it).
    // For subsequent grants, the doctor needs to have the extractable key.
    // Since we can't make unwrapped keys extractable, the practical approach is:
    // - On first creation, cache the EXTRACTABLE key
    // - On re-grant (returning session), decrypt our envelope to get the raw key, re-import as extractable
    //
    // The simplest correct approach: Use encrypt() for re-wrapping (same as the original code).
    // Import the target's public key with 'encrypt' usage and encrypt the raw key bytes.
    // To get raw bytes, we export our case key — but it's non-extractable!
    //
    // Real solution: When creating the envelope, we must also store the case key in an extractable form.
    // OR: We unwrap our envelope with extractable:true specifically for this operation.

    // Unwrap our own envelope again, but this time with extractable:true for re-wrapping
    const myEnvelope = await apiClient.getMyCaseEnvelope(caseId);
    if (!myEnvelope) {
      throw new Error('Own envelope not found');
    }

    const extractableCaseKey = await caseKeyService.unwrapCaseKeyExtractable(
      myEnvelope.encryptedCaseKey,
      cachedPrivateKeyPem,
    );

    // Wrap the extractable case key with the target's public key
    const wrappedForTarget = await caseKeyService.wrapCaseKey(
      extractableCaseKey,
      targetPublicKeyPem,
    );

    // Store the envelope for the target user
    await apiClient.createCaseEnvelope(caseId, targetUserId, wrappedForTarget);
  };

  /**
   * Encrypt data using the per-case key (envelope encryption).
   * Falls back to passphrase-derived key if no envelope exists.
   */
  const encryptForCase = async (caseId: string, data: object): Promise<string> => {
    if (!isUnlocked) {
      throw new Error('Encryption not unlocked. Please enter your passphrase.');
    }

    const caseKey = await getCaseKey(caseId);
    if (!caseKey) {
      throw new Error(
        `No case key available for case ${caseId}. Ensure an envelope exists before encrypting.`,
      );
    }
    return caseKeyService.encryptWithCaseKey(data, caseKey);
  };

  /**
   * Decrypt data using the per-case key (envelope encryption).
   * Throws if no case key is available — callers must handle the error.
   */
  const decryptForCase = async <T extends object = object>(
    caseId: string,
    ciphertext: string,
  ): Promise<T> => {
    if (!isUnlocked) {
      throw new Error('Encryption not unlocked. Please enter your passphrase.');
    }

    const caseKey = await getCaseKey(caseId);
    if (!caseKey) {
      throw new Error(
        `No case key available for case ${caseId}. You may not have access to this case.`,
      );
    }
    return caseKeyService.decryptWithCaseKey<T>(ciphertext, caseKey);
  };

  /** Clear all cached case keys from memory */
  const clearCaseKeyCache = () => {
    caseKeyEpoch.current++;
    caseKeyCache.current.clear();
    caseKeyPendingRequests.current.clear();
  };

  /**
   * Re-encrypt case fields from field-level RSA → envelope encryption.
   * Uses the doctor's cached private key to decrypt old fields, then re-encrypts
   * with the per-case AES key. Returns the re-encrypted fields (CASEKEY: prefixed).
   * Caller is responsible for POSTing these to the re-encrypt endpoint.
   */
  const reEncryptCaseFields = async (
    caseId: string,
    rawCaseData: Record<string, any>,
  ): Promise<ReEncryptionResult> => {
    if (!isUnlocked || !cachedPrivateKeyPem) {
      throw new Error('Encryption not unlocked. Cannot re-encrypt.');
    }

    const caseKey = await getCaseKey(caseId);
    if (!caseKey) {
      throw new Error('No case key available for re-encryption.');
    }

    return await reEncryptCaseToEnvelope(rawCaseData, caseKey, cachedPrivateKeyPem);
  };

  /**
   * Re-encrypt intake request fields from field-level RSA → envelope encryption.
   * Uses the doctor's cached private key to decrypt old fields, then re-encrypts
   * with the per-case AES key. Returns the re-encrypted fields (CASEKEY: prefixed).
   */
  const reEncryptIntakeRequestFields = async (
    caseId: string,
    rawRequestData: Record<string, any>,
  ): Promise<ReEncryptionResult> => {
    if (!isUnlocked || !cachedPrivateKeyPem) {
      throw new Error('Encryption not unlocked. Cannot re-encrypt.');
    }

    const caseKey = await getCaseKey(caseId);
    if (!caseKey) {
      throw new Error('No case key available for re-encryption.');
    }

    return await reEncryptIntakeRequestToEnvelope(rawRequestData, caseKey, cachedPrivateKeyPem);
  };

  /**
   * Suppress the auto-show of PassphraseSetup modal.
   * Called by signup forms that handle encryption setup themselves.
   */
  const suppressSetupModal = () => {
    setupModalSuppressed.current = true;
    setShowSetup(false); // Also dismiss if already showing
  };

  // Derived: true when an encryption modal (setup or unlock) is visible to the user.
  // Exposed so Dashboard/AttorneyDashboard can defer onboarding modals and tours.
  const isShowingModal = (showSetup || showUnlock) && !hasServiceError;

  const contextValue: EncryptionContextValue = {
    status,
    isUnlocked,
    isLoading,
    hasServiceError,
    isShowingModal,
    setupEncryption,
    unlockEncryption,
    lockEncryption,
    clearKeyMaterial,
    refreshStatus,
    retryStatusLoad,
    suppressSetupModal,
    encryptPayload,
    decryptPayload,
    decryptWithPrivateKey,
    decryptField,
    getCaseKey,
    createCaseEnvelope,
    grantCaseAccess,
    encryptForCase,
    decryptForCase,
    clearCaseKeyCache,
    reEncryptCaseFields,
    reEncryptIntakeRequestFields,
  };

  return (
    <EncryptionContext.Provider value={contextValue}>
      {/* Show setup modal if needed - but not if service has errors */}
      {showSetup && !hasServiceError && (
        <PassphraseSetup
          onComplete={setupEncryption}
          onCancel={requireEncryption ? undefined : () => setShowSetup(false)}
        />
      )}

      {/* Show unlock modal if needed - but not if service has errors */}
      {showUnlock && !hasServiceError && (
        <PassphraseEntry
          onSuccess={unlockEncryption}
          allowCancel={!requireEncryption}
          onCancel={requireEncryption ? undefined : () => setShowUnlock(false)}
          onSignOut={requireEncryption ? () => signOut() : undefined}
          onForgot={() => {
            toast.error('Passphrase cannot be recovered', {
              description:
                'If you lost your passphrase, your encrypted data cannot be accessed. Contact support for account recovery options.',
              duration: 8000,
            });
          }}
        />
      )}

      {/* Show children (app content).
          The full-screen "Loading encryption status" blocker only makes
          sense for a signed-in user whose encryption state we still need
          to fetch. For unauthenticated visitors on marketing pages
          (Landing, /pricing, /how-it-works, /security, etc.) the
          spinner was showing during the brief Clerk auth-resolution
          window and blocking the whole page — user feedback 2026-08-17.
          Rendering children immediately when the user isn't signed in
          hands control to whichever page-level component actually
          needs to render (Auth, Landing, etc.). */}
      {isLoading && isSignedIn === true ? (
        <div className="flex min-h-screen items-center justify-center bg-[#FAF7F0]">
          <div className="space-y-4 text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#A66B32] border-t-transparent" />
            <p className="font-editorial text-sm text-[#3A2718]/70">Loading encryption status...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </EncryptionContext.Provider>
  );
}

/**
 * Use Encryption Hook
 *
 * Access encryption context from any component.
 *
 * @example
 * const { isUnlocked, encryptPayload, decryptPayload } = useEncryption();
 */
export function useEncryption() {
  const context = useContext(EncryptionContext);

  if (!context) {
    throw new Error('useEncryption must be used within EncryptionProvider');
  }

  return context;
}
